import React, { useState, useEffect } from 'react';
import { CheckCircle, AlertCircle, Loader2, Mail, Calendar, Users, TrendingUp, Store, DollarSign } from 'lucide-react';
import { initCsrfToken, api } from '../services/api';
import { extractApiError } from '../utils/errorUtils'; // Issue #964: Standardized error handling

interface SignupFormData {
  email: string;
  userType: 'fair_manager' | 'vendor' | '';
  referralSource: string;
}

const PreLaunchPage: React.FC = () => {
  const [formData, setFormData] = useState<SignupFormData>({
    email: '',
    userType: '',
    referralSource: ''
  });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Initialize CSRF token on component mount (Issue #197)
  useEffect(() => {
    const initializeCsrf = async () => {
      try {
        await initCsrfToken();
      } catch (error: unknown) {
        console.error('Failed to initialize CSRF token:', error);
        setError('Failed to initialize security token. Please refresh the page.');
      }
    };

    initializeCsrf();
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      // Use api instance which includes CSRF token interceptor (Issue #197)
      await api.post('/prelaunch/signup', {
        email: formData.email,
        user_type: formData.userType,
        referral_source: formData.referralSource || null
      });

      setSuccess(true);
      setFormData({ email: '', userType: '', referralSource: '' });
    } catch (err: unknown) {
      const apiError = extractApiError(err);
      setError(apiError.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-gradient-to-br from-moonbone via-white to-fae-blush/20">
      {/* Hero Section */}
      <div className="relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-24">
          <div className="text-center">
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-gray-900 mb-6">
              Connecting Artists with{' '}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-dusty-teal to-dusk-rose">
                Local Fairs
              </span>
            </h1>
            <p className="text-xl sm:text-2xl text-gray-600 mb-8 max-w-3xl mx-auto">
              The modern platform for discovering, managing, and promoting artist fairs and craft markets.
            </p>
            <p className="text-lg text-gray-700 mb-12 font-medium">
              Coming Soon! Be the first to know when we launch.
            </p>

            {/* Signup Form */}
            {!success ? (
              <div className="max-w-md mx-auto bg-white rounded-2xl shadow-xl p-8">
                <div className="flex items-center justify-center mb-6">
                  <Mail className="w-8 h-8 text-dusty-teal mr-2" />
                  <h2 className="text-2xl font-bold text-gray-900">Get Early Access</h2>
                </div>

                <form onSubmit={handleSubmit} className="space-y-4">
                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                      Email Address *
                    </label>
                    <input
                      type="email"
                      id="email"
                      required
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-dusty-teal focus:border-transparent"
                      placeholder="you@example.com"
                      disabled={loading}
                    />
                  </div>

                  <div>
                    <label htmlFor="userType" className="block text-sm font-medium text-gray-700 mb-1">
                      I am a... *
                    </label>
                    <select
                      id="userType"
                      required
                      value={formData.userType}
                      onChange={(e) => setFormData({ ...formData, userType: e.target.value as 'fair_manager' | 'vendor' })}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-dusty-teal focus:border-transparent"
                      disabled={loading}
                    >
                      <option value="">Select your role</option>
                      <option value="fair_manager">Fair Organizer / Manager</option>
                      <option value="vendor">Artist / Vendor / Craftsperson</option>
                    </select>
                  </div>

                  <div>
                    <label htmlFor="referralSource" className="block text-sm font-medium text-gray-700 mb-1">
                      How did you hear about us? (optional)
                    </label>
                    <input
                      type="text"
                      id="referralSource"
                      value={formData.referralSource}
                      onChange={(e) => setFormData({ ...formData, referralSource: e.target.value })}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-dusty-teal focus:border-transparent"
                      placeholder="Social media, friend, search, etc."
                      maxLength={100}
                      disabled={loading}
                    />
                  </div>

                  {error && (
                    <div className="flex items-start bg-red-50 border border-red-200 rounded-lg p-4">
                      <AlertCircle className="w-5 h-5 text-red-600 mr-2 flex-shrink-0 mt-0.5" />
                      <span className="text-sm text-red-800">{error}</span>
                    </div>
                  )}

                  <button
                    type="submit"
                    disabled={loading}
                    className="w-full bg-gradient-to-r from-dusty-teal to-dusk-rose text-white font-semibold py-3 px-6 rounded-lg hover:from-dusty-teal/90 hover:to-dusk-rose/90 transition-all duration-200 shadow-lg disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
                  >
                    {loading ? (
                      <>
                        <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                        Signing up...
                      </>
                    ) : (
                      <>
                        <Mail className="w-5 h-5 mr-2" />
                        Notify Me at Launch
                      </>
                    )}
                  </button>
                </form>

                <p className="text-xs text-gray-500 mt-4 text-center">
                  We'll only email you when we launch. No spam, ever.
                </p>
              </div>
            ) : (
              <div className="max-w-md mx-auto bg-white rounded-2xl shadow-xl p-8">
                <div className="flex flex-col items-center text-center">
                  <div className="bg-sage/20 rounded-full p-3 mb-4">
                    <CheckCircle className="w-12 h-12 text-sage" />
                  </div>
                  <h2 className="text-2xl font-bold text-gray-900 mb-2">You're on the list!</h2>
                  <p className="text-gray-600 mb-6">
                    Thank you for your interest. We'll notify you as soon as we launch!
                  </p>
                  <button
                    onClick={() => setSuccess(false)}
                    className="text-dusty-teal hover:text-dusty-teal/80 font-medium"
                  >
                    Sign up another email
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Features Section - Fair Managers */}
      <div className="bg-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
              For Fair Organizers & Managers
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Powerful tools to manage your events, vendors, and logistics.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <div className="bg-dusty-teal/10 rounded-xl p-6 hover:shadow-lg transition-shadow">
              <div className="bg-dusty-teal/20 rounded-lg w-12 h-12 flex items-center justify-center mb-4">
                <Calendar className="w-6 h-6 text-dusty-teal" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">Event Management</h3>
              <p className="text-gray-600">
                Easily create and manage multi-day fairs with customizable schedules, vendor booths, and amenities.
              </p>
            </div>

            <div className="bg-dusty-teal/10 rounded-xl p-6 hover:shadow-lg transition-shadow">
              <div className="bg-dusty-teal/20 rounded-lg w-12 h-12 flex items-center justify-center mb-4">
                <Users className="w-6 h-6 text-dusty-teal" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">Vendor Management</h3>
              <p className="text-gray-600">
                Approve reservations, manage booth assignments, and communicate with vendors all in one place.
              </p>
            </div>

            <div className="bg-dusty-teal/10 rounded-xl p-6 hover:shadow-lg transition-shadow">
              <div className="bg-dusty-teal/20 rounded-lg w-12 h-12 flex items-center justify-center mb-4">
                <TrendingUp className="w-6 h-6 text-dusty-teal" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">Analytics & Insights</h3>
              <p className="text-gray-600">
                Track reservations, revenue, occupancy rates, and vendor performance with real-time dashboards.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Features Section - Vendors */}
      <div className="bg-gradient-to-br from-fae-blush/10 to-dusty-teal/10 py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
              For Artists & Vendors
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Find fairs, manage your booth reservations, and grow your business.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <div className="bg-white rounded-xl p-6 hover:shadow-lg transition-shadow">
              <div className="bg-dusty-teal/20 rounded-lg w-12 h-12 flex items-center justify-center mb-4">
                <Store className="w-6 h-6 text-dusty-teal" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">Discover Fairs</h3>
              <p className="text-gray-600">
                Browse upcoming fairs, filter by location and dates, and find the perfect events for your products.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 hover:shadow-lg transition-shadow">
              <div className="bg-dusty-teal/20 rounded-lg w-12 h-12 flex items-center justify-center mb-4">
                <Calendar className="w-6 h-6 text-dusty-teal" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">Easy Reservations</h3>
              <p className="text-gray-600">
                Reserve booth spaces online, choose full-duration or per-day options, and track all your bookings.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 hover:shadow-lg transition-shadow">
              <div className="bg-dusty-teal/20 rounded-lg w-12 h-12 flex items-center justify-center mb-4">
                <DollarSign className="w-6 h-6 text-dusty-teal" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">Vendor Profile</h3>
              <p className="text-gray-600">
                Showcase your work with portfolio galleries, manage products, and build your brand presence.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Social Proof Section */}
      <div className="bg-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold text-gray-900 mb-8">
            Join the Future of Artist Fairs
          </h2>
          <p className="text-lg text-gray-600 max-w-3xl mx-auto mb-8">
            We're building a platform that makes it easier than ever to organize, discover, and participate in artist fairs and craft markets. Be part of the community from day one.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 text-gray-700">
            <div className="flex items-center">
              <CheckCircle className="w-5 h-5 text-sage mr-2" />
              <span>Fair Management Tools</span>
            </div>
            <div className="flex items-center">
              <CheckCircle className="w-5 h-5 text-sage mr-2" />
              <span>Vendor Reservations</span>
            </div>
            <div className="flex items-center">
              <CheckCircle className="w-5 h-5 text-sage mr-2" />
              <span>Portfolio Galleries</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PreLaunchPage;
